"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useSmartBack } from "@/lib/use-smart-back"
import { Badge } from "@/components/ui/badge"
import { ArrowLeft, ChevronDown, Clock } from "@/lib/icons"
import Image from "next/image"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

interface RoadmapItem {
  id: string
  title: string
  description: string | null
  status: "planned" | "in_progress" | "testing" | "done"
  timeline: string | null
  updated_at: string
}

const STATUS_LABEL: Record<string, string> = {
  planned: "Planned",
  in_progress: "In Progress",
  testing: "Testing",
  done: "Done",
}

const STATUS_STYLE: Record<string, string> = {
  planned:     "border-purple-500/40 bg-purple-500/10 text-purple-300",
  in_progress: "border-blue-500/40 bg-blue-500/10 text-blue-300",
  testing:     "border-amber-500/40 bg-amber-500/10 text-amber-300",
  done:        "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
}

const STATUS_DOT: Record<string, string> = {
  planned:     "bg-purple-400",
  in_progress: "bg-blue-400 animate-pulse",
  testing:     "bg-amber-400",
  done:        "bg-emerald-400",
}

export default function RoadmapPage() {
  const t = useTranslations("roadmapPage")
  const goBack = useSmartBack()
  const [items, setItems] = useState<RoadmapItem[]>([])
  const [loading, setLoading] = useState(true)
  // Collapsed by default — a full roadmap of descriptions gets long fast.
  // Tracks which ids are EXPANDED rather than collapsed, so a freshly
  // loaded list (nothing in the set yet) starts fully condensed.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  useEffect(() => {
    fetch("/api/roadmap")
      .then((res) => res.json())
      .then((data) => setItems(data.roadmapItems || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="fixed inset-0 overflow-hidden bg-black">

      {/* ── Content layer — behind border ── */}
      <div
        className="absolute z-10 flex flex-col"
        style={{
          top: "clamp(10px, 2vh, 24px)",
          bottom: "clamp(36px, 5vh, 60px)",
          left: "clamp(16px, 4vw, 52px)",
          right: "clamp(16px, 4vw, 52px)",
        }}
      >
        {/* ── Sticky header ── */}
        <div className="flex-shrink-0 pb-3 pt-8">
          <div className="flex flex-col items-center text-center gap-2">
            <div className="relative w-32 h-32 select-none pointer-events-none">
              <Image
                src="/mascot/glym-road2.webp"
                alt=""
                fill
                className="object-contain"
                priority
                unoptimized
              />
            </div>

            <h1
              className="text-4xl md:text-5xl font-black tracking-tight"
              style={{
                background: "linear-gradient(135deg, #f5c518 0%, #fde68a 50%, #f5c518 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                filter: "drop-shadow(0 0 12px rgba(245,197,24,0.35))",
              }}
            >
              {t("roadmap")}
            </h1>

            <p className="text-white/70 text-xs">
              {t("whatWeReBuildingFixingAndPlanningNext")}
            </p>
          </div>

          <div className="mt-3 h-px bg-white/10" />
        </div>

        {/* ── Scrollable items list ── */}
        <div className="flex-1 overflow-y-auto min-h-0 py-2">
          <div className="max-w-lg mx-auto w-full">
            {loading ? (
              <div className="flex flex-col gap-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 rounded-2xl bg-white/5 animate-pulse" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="text-center text-white/50 py-12">
                <p className="text-lg">{t("nothingHereYet")}</p>
                <p className="text-sm mt-1">{t("checkBackSoon")}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3 pb-2">
                {items.map((item) => {
                  const expanded = expandedIds.has(item.id)
                  const hasDetails = Boolean(item.description || item.timeline)
                  return (
                    <div
                      key={item.id}
                      className="rounded-2xl border border-white/10 bg-black/50 backdrop-blur-md p-4 shadow-[0_0_20px_rgba(0,0,0,0.5)]"
                    >
                      <button
                        type="button"
                        onClick={() => hasDetails && toggleExpanded(item.id)}
                        className={cn("flex w-full items-start justify-between gap-3 text-left", !hasDetails && "cursor-default")}
                        aria-expanded={expanded}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className={cn("w-2 h-2 rounded-full flex-shrink-0 mt-0.5", STATUS_DOT[item.status])} />
                          <h2 className="text-sm font-semibold text-white leading-snug">
                            {item.title}
                          </h2>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1.5">
                          <Badge
                            variant="outline"
                            className={cn("text-[10px] px-2 py-0 border", STATUS_STYLE[item.status])}
                          >
                            {STATUS_LABEL[item.status]}
                          </Badge>
                          {hasDetails && (
                            <ChevronDown className={cn("h-3.5 w-3.5 text-white/40 transition-transform", expanded && "rotate-180")} />
                          )}
                        </div>
                      </button>

                      {expanded && item.description && (
                        <p className="text-xs text-white/65 mt-2 leading-relaxed whitespace-pre-wrap pl-4">
                          {item.description}
                        </p>
                      )}

                      {expanded && item.timeline && (
                        <p className="text-[10px] text-white/45 mt-3 flex items-center gap-1 pl-4">
                          <Clock className="h-3 w-3" />
                          {item.timeline}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Border layer — on top of content, transparent center lets content show through ── */}
      <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
        {/* Scale up slightly so drips are thinner / less of the frame shows */}
        {/* Mobile portrait — < 768px */}
        <div className="absolute md:hidden" style={{ inset: "-3% -3%" }}>
          <Image
            src="/borders/goopborder-alt-portrait.webp"
            alt=""
            fill
            style={{ objectFit: "fill" }}
            priority
            unoptimized
          />
        </div>
        {/* Tablet — 768px–1023px */}
        <div className="absolute hidden md:block lg:hidden" style={{ inset: "-3% -3%" }}>
          <Image
            src="/borders/goopborder-alt-tablet.webp"
            alt=""
            fill
            style={{ objectFit: "fill" }}
            priority
            unoptimized
          />
        </div>
        {/* Ultrawide / desktop — 1024px+ */}
        <div className="absolute hidden lg:block" style={{ inset: "-3% -3%" }}>
          <Image
            src="/borders/goopborder-alt-ultrawide.webp"
            alt=""
            fill
            style={{ objectFit: "fill" }}
            priority
            unoptimized
          />
        </div>
      </div>

      {/* ── Back button — above border ── */}
      <button
        type="button"
        onClick={goBack}
        className="absolute z-30 top-4 left-8 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-sm border border-white/10"
        aria-label={t("back")}
      >
        <ArrowLeft className="h-4 w-4 text-white" />
      </button>

    </div>
  )
}
